import React from 'react';
import find from "lodash/find";
import UserRating from "./UserRating";
import ReviewTags from "./ReviewTags";
import MultiSelectField from "@Generic/form/MultiSelectField";
import convertObjectToArrayOfObjects from "@Object/convertObjectToArrayOfObjects";
import toggleArrayMember from "../../../utils/array/toggleArrayMember";
import Translation from "@Translation/components/Translation";
import IconTimes from "@Icons/controls/IconTimes";
import {useTranslation} from "@Translation/hooks/useTranslation";
import {useAppSelector} from "@Hooks/selector/useAppSelector";
import FiltersType from "@Pages/reviews/types/Filters.type";
import {RatingType} from "@Pages/reviews/types/Rating.type";
import ReviewStationType from "@Pages/reviews/types/ReviewStation.type";
import MultiSelectFieldStations from "@Pages/reviews/components/MultiSelectFieldStations";
import useStationsAutoSuggest from "@Pages/reviews/hooks/useStationsAutoSuggest";
import {useToggle} from "@Hooks/dispatch/useToggle";
import Column from "@Containers/components/Column";
import Row from "@Containers/components/Row";

interface ReviewFiltersProps {
    activeFilters: FiltersType,
    handleInputFieldUpdate: (any) => void,
    languageHandler: (any) => void,
    reviewScoreBreakdown: {
        score1: string,
        score2: string,
        score3: string,
        score4: string,
        score5: string,
        scoreTotal: string,
    },
    stationHandler: (station: ReviewStationType) => void,
    activeStationHandler: (stations: string[]) => void,
    stations: ReviewStationType[],
    tagHandler: (any) => void,
    userRatingHandler: (rating: RatingType) => void,
    verifiedHandler: () => void,
    clearAllFilters: () => void
}

function ReviewFilters(props: ReviewFiltersProps) {
    const allLanguages = useAppSelector((state) => state?.page.activeLanguages)
    const {
        excellent,
        very_good: veryGood,
        good,
        mediocre,
        unacceptable,
    } = useTranslation("place_review.rating_grades")
    const reviews = useTranslation("c_profile.reviews")
    const companyLabel = useTranslation("generic.terms.company_related.company")
    const searchByCompanyName = useTranslation("c_profile.search_by_company_name")
    const selectRouteStations = useTranslation("c_profile.select_route_stations")
    const selectLanguages = useTranslation("c_profile.select_languages")
    const clearAll = useTranslation("global_review_page.filter_notification.clear_all")
    const youHave = useTranslation("global_review_page.filter_notification.you_have")
    const activeFiltersMessage = useTranslation("global_review_page.filter_notification.active_filters")

    const {
        score1,
        score2,
        score3,
        score4,
        score5,
        scoreTotal,
    } = props.reviewScoreBreakdown

    const {
        stations,
        handleInputFieldUpdate,
        userRatingHandler,
        tagHandler,
        activeStationHandler,
        activeFilters,
        languageHandler,
        verifiedHandler,
        stationHandler,
        clearAllFilters,
    } = props

    const {
        tags: activeTags,
        stations: activeStations,
        languages: activeLanguages,
        verified,
        company,
        ratings,
    } = activeFilters

    const stationsToggle = useToggle()

    const toggleVisibility = () => stationsToggle.onChange()
    const autoSuggestModel = useStationsAutoSuggest(activeStations, activeStationHandler, toggleVisibility, stationHandler)

    const roundScore = (score) => (Math.round((score / parseFloat(scoreTotal)) * 100))

    const numberOfFilters = Object.keys(activeFilters).reduce(
        (totalNumber, filter) => {
            if (Array.isArray(activeFilters[filter])) {
                return totalNumber + activeFilters[filter].length;
            } else if (!Array.isArray(activeFilters[filter]) && activeFilters[filter] != '' && activeFilters[filter] != 0) {
                return totalNumber + 1;
            }
            return totalNumber
        }, 0
    )

    return (
        <section className="gb--card-reviews-filter gb--card-default">
            <div className=" gb--row gb--row-justify">
                <Column alignStart>
                    <h2 className={"gb--filters-title"}>{reviews}</h2>
                </Column>
                <div className="gb--toggle gb--row gb--row-middle">
                    <input
                        type="checkbox"
                        id="verifiedReviews"
                        checked={!!verified}
                        readOnly
                    />
                    <button
                        type="button"
                        className="gb--toggle-switch gb--toggle-switch-blue gb--toggle-switch-left"
                        onClick={verifiedHandler}
                    >
                        <span className="gb--toggle-slider"/>
                        <span className="gb--toggle-mark"/>
                    </button>
                    <span>
                        <Translation translationKey="c_profile.company_reviews.verified_reviews_only"/>
                    </span>
                </div>
            </div>
            <hr/>
            <div className="gb--grid-2c">
                <div className="gb--column">
                    <div>
                        <span className="gb--input-label">{companyLabel}</span>
                        <input
                            type="text"
                            name="company"
                            onChange={handleInputFieldUpdate}
                            placeholder={searchByCompanyName}
                            className="gb--input-standalone"
                            value={company}
                        />
                    </div>
                    <div>
                        <span className="gb--input-label">
                            <Translation translationKey="c_profile.company_reviews.language"/>
                        </span>
                        <MultiSelectField
                            options={convertObjectToArrayOfObjects(allLanguages)}
                            value="id"
                            label="name"
                            handler={languageHandler}
                            selected={activeLanguages}
                            placeholder={selectLanguages}
                        >
                            {activeLanguages.map((language) => (
                                <button
                                    type="button"
                                    className="gb--tag gb--tag-default"
                                    key={language}
                                    onClick={toggleArrayMember(languageHandler, activeLanguages, language)}
                                >
                                    {allLanguages[language]}
                                    <IconTimes
                                        width="14"
                                        height="12"
                                        fill="#070c63"
                                    />
                                </button>
                            ))}
                        </MultiSelectField>
                    </div>
                    <div>
                        <span className="gb--input-label">
                            <Translation translationKey="c_profile.company_reviews.stations"/>
                        </span>
                        <MultiSelectFieldStations
                            placeholder={selectRouteStations}
                            autoSuggestModel={autoSuggestModel}
                            visible={stationsToggle}
                        >
                            {activeStations.map((station) => (
                                <button
                                    type="button"
                                    className="gb--tag gb--tag-default"
                                    key={station}
                                    onClick={toggleArrayMember(activeStationHandler, activeStations, station)}
                                >
                                    {find(stations, {id: station}).name}
                                    <IconTimes
                                        width="14"
                                        height="12"
                                        fill="#070c63"
                                    />
                                </button>
                            ))}
                        </MultiSelectFieldStations>
                    </div>
                </div>
                <div className="gb--column">
                    <div className="gb--column">
                        <span className="gb--input-label">
                            <Translation translationKey="c_profile.company_reviews.user_ratings"/>
                        </span>
                        <UserRating
                            label={excellent}
                            value={roundScore(score5)}
                            onClick={userRatingHandler}
                            rating="8"
                            activeRatings={ratings}
                        />
                        <UserRating
                            label={veryGood}
                            value={roundScore(score4)}
                            onClick={userRatingHandler}
                            rating="6"
                            activeRatings={ratings}
                        />
                        <UserRating
                            label={good}
                            value={roundScore(score3)}
                            onClick={userRatingHandler}
                            rating="4"
                            activeRatings={ratings}
                        />
                        <UserRating
                            label={mediocre}
                            value={roundScore(score2)}
                            onClick={userRatingHandler}
                            rating="2"
                            activeRatings={ratings}
                        />
                        <UserRating
                            label={unacceptable}
                            value={roundScore(score1)}
                            onClick={userRatingHandler}
                            rating="0"
                            activeRatings={ratings}
                        />
                    </div>
                </div>
            </div>
            <ReviewTags
                tagHandler={tagHandler}
                activeTags={activeTags}
            />
            <Row justify className="gb--notification gb--notification-blue">
                <span className="gb--filters-notification">
                    {youHave}
                    <span className="gb--emphasize">{numberOfFilters}</span>
                    {activeFiltersMessage}
                </span>
                {numberOfFilters !== 0 && (
                    <button
                        type="button"
                        onClick={clearAllFilters}
                    >
                        <span className="gb--emphasize">{clearAll}</span>
                    </button>
                )}
            </Row>
        </section>
    )
}

export default ReviewFilters;